// Global Verification Banner - Shows on all pages
import React, { useState, useEffect } from 'react';
import {
  Box,
  Alert,
  Button,
  LinearProgress,
  Typography,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
  alpha,
  Card,
  CardContent,
  Fade,
  CircularProgress,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SecurityIcon from '@mui/icons-material/Security';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

export default function VerificationBanner({ 
  user,
  isRefreshing,
  forceShow,
  onStartMFA, 
  onLearnMore,
  onDismiss,
  onRetryVerification 
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [dismissed, setDismissed] = useState(false);

  // Debug: Log when user prop changes
  useEffect(() => {
    // Component rendered/updated
  }, [user?.verified, user?.verificationStatus, user?.mfaRequired, user?.mfaCodeSent, dismissed]);

  if (!user || dismissed) {
    return null;
  }

  const { verified, mfaRequired, verificationStatus, mfaCodeSent, verificationError } = user;

  // Don't show if already verified (unless we're refreshing subscriptions or forceShow is true)
  if (verified && !isRefreshing && !forceShow) {
    return null;
  }

  // Handle different verification states
  const getAlertContent = () => {
    // Verification failed - wrong credentials
    if (verificationStatus === 'failed') {
      return {
        severity: 'error',
        icon: null,
        title: 'Verification Failed',
        message: verificationError || 'Invalid OnlyFans credentials. Please update and try again.',
        action: (
          <Button
            color="inherit"
            size="small"
            variant="outlined"
            onClick={onRetryVerification}
            sx={{ 
              fontWeight: 600,
              borderWidth: 2,
              '&:hover': { borderWidth: 2 }
            }}
          >
            Retry Verification
          </Button>
        ),
        showProgress: false,
      };
    }
    
    // MFA code sent - waiting for user to enter
    if (mfaCodeSent) {
      return {
        severity: 'warning',
        icon: <SecurityIcon />,
        title: 'MFA Code Required',
        message: 'Check your OnlyFans email/app for a verification code.',
        action: null,
        showProgress: false,
      };
    }

    // MFA verification in progress
    if (verificationStatus === 'mfa_verification_triggered') {
      return {
        severity: 'info',
        icon: null,
        title: 'Sending Verification Code...',
        message: 'Check your OnlyFans account in 10-30 seconds.',
        action: null,
        showProgress: true,
      };
    }

    // MFA code submitted, processing
    if (verificationStatus === 'mfa_code_submitted') {
      return {
        severity: 'info',
        icon: null,
        title: 'Verifying Your Account...',
        message: 'Fetching your subscriptions. This takes 1-2 minutes.',
        action: null,
        showProgress: true,
      };
    }

    // MFA required but user hasn't triggered yet
    if (mfaRequired) {
      return {
        severity: 'warning',
        icon: <SecurityIcon />,
        title: 'OnlyFans Verification Needed',
        message: 'Your account has 2FA. Click to verify when ready.',
        action: (
          <Button
            color="inherit"
            size="small"
            variant="outlined"
            onClick={onStartMFA}
            sx={{ 
              fontWeight: 600,
              borderWidth: 2,
              '&:hover': { borderWidth: 2 }
            }}
          >
            Start Verification
          </Button>
        ),
        showProgress: false,
      };
    }

    // Processing statuses that should show progress (or forceShow is true)
    if (['processing', 'queued', 'mfa_triggered'].includes(verificationStatus) || forceShow) {
      // Different message if this is a subscription refresh vs initial verification
      const title = isRefreshing ? 'Refreshing Subscriptions' : 'Verification in Progress';
      const message = isRefreshing 
        ? 'Updating your OnlyFans subscriptions. This may take a few minutes.'
        : 'We\'re processing your verification. This may take a few minutes.';
      
      return {
        severity: 'info',
        icon: null,
        title,
        message,
        action: null,
        showProgress: true,
      };
    }

    // Default - don't show banner for 'unverified' or other statuses
    return null;
  };

  const content = getAlertContent();
  
  // Don't render if no content to show
  if (!content) {
    return null;
  }

  const handleDismiss = () => {
    setDismissed(true);
    if (onDismiss) onDismiss();
  };

  // Get background gradient based on severity
  const getGradient = () => {
    switch(content.severity) {
      case 'error':
        return 'linear-gradient(135deg, #ef5350 0%, #e57373 100%)'; // Softer red
      case 'warning':
        return 'linear-gradient(135deg, #ff9800 0%, #ff5722 100%)';
      case 'info':
      default:
        return 'linear-gradient(135deg, #64b5f6 0%, #42a5f5 100%)'; // Baby blue
    }
  };

  return (
    <Fade in timeout={500}>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 1099,
          background: getGradient(),
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          overflow: 'hidden',
        }}
      >
        {/* Animated background pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            background: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px)',
            animation: 'slide 20s linear infinite',
            '@keyframes slide': {
              '0%': { transform: 'translateX(0)' },
              '100%': { transform: 'translateX(40px)' },
            },
          }}
        />

        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 2, md: 3 },
            py: { xs: 2.5, sm: 3 },
            px: { xs: 2, sm: 4 },
            flexWrap: { xs: 'wrap', md: 'nowrap' },
          }}
        >
          {/* Animated spinner rings */}
          {content.showProgress && (
            <Box
              sx={{
                position: 'relative',
                minWidth: { xs: 50, sm: 60 },
                width: { xs: 50, sm: 60 },
                height: { xs: 50, sm: 60 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/* Outer ring */}
              <Box
                sx={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  border: '3px solid rgba(255, 255, 255, 0.2)',
                  borderTopColor: 'rgba(255, 255, 255, 0.8)',
                  animation: 'spin 1.5s linear infinite',
                  '@keyframes spin': {
                    to: { transform: 'rotate(360deg)' },
                  },
                }}
              />
              {/* Middle ring */}
              <Box
                sx={{
                  position: 'absolute',
                  width: '75%',
                  height: '75%',
                  borderRadius: '50%',
                  border: '3px solid rgba(255, 255, 255, 0.15)',
                  borderTopColor: 'rgba(255, 255, 255, 0.7)',
                  animation: 'spin-reverse 1s linear infinite',
                  '@keyframes spin-reverse': {
                    to: { transform: 'rotate(-360deg)' },
                  },
                }}
              />
              {/* Inner ring */}
              <Box
                sx={{
                  position: 'absolute',
                  width: '50%',
                  height: '50%',
                  borderRadius: '50%',
                  border: '2px solid rgba(255, 255, 255, 0.1)',
                  borderTopColor: 'rgba(255, 255, 255, 0.6)',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
              {/* Center pulse */}
              <Box
                sx={{
                  width: '30%',
                  height: '30%',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.9)',
                  animation: 'pulse-center 2s ease-in-out infinite',
                  '@keyframes pulse-center': {
                    '0%, 100%': {
                      transform: 'scale(1)',
                      opacity: 0.8,
                    },
                    '50%': {
                      transform: 'scale(1.3)',
                      opacity: 1,
                    },
                  },
                }}
              />
            </Box>
          )}

          {/* Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{
                color: 'white',
                textShadow: '0 2px 8px rgba(0, 0, 0, 0.3), 0 1px 2px rgba(0, 0, 0, 0.2)',
                WebkitTextStroke: '0.5px rgba(0, 0, 0, 0.1)',
                mb: 0.5,
                fontSize: { xs: '1rem', sm: '1.125rem' },
              }}
            >
              {content.title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'white',
                textShadow: '0 1px 4px rgba(0, 0, 0, 0.25), 0 1px 2px rgba(0, 0, 0, 0.15)',
                fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                fontWeight: 500,
              }}
            >
              {content.message}
            </Typography>
          </Box>

          {/* Actions */}
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            {content.action && (
              <Button
                onClick={content.action.props.onClick}
                size="medium"
                sx={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  color: '#1976d2',
                  fontWeight: 700,
                  px: 3,
                  py: 1,
                  borderRadius: 3,
                  textTransform: 'none',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  transition: 'all 0.2s',
                  '&:hover': {
                    background: 'white',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
                  },
                }}
              >
                {content.action.props.children}
              </Button>
            )}
            <IconButton
              size="small"
              onClick={handleDismiss}
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Fade>
  );
}

